import { useLocation, useNavigate } from '@tanstack/react-router'
import type { ParseKeys, TFunction } from 'i18next'
import { MotionConfig } from 'motion/react'
import { lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MaintenanceNotices } from '@ventawiz/web-kit'

import { PANEL_RESERVE_CLASS, panelReserveStyle } from '@/components/AiAnalyst/panelPreference'
import { pageSearchOf } from '@/components/AiAnalyst/pageContext'
import { useAnalystDock } from '@/components/AiAnalyst/useAnalystDock'
import { Breadcrumbs, HeaderTrailProvider } from '@/components/common/page/Breadcrumbs'
import { AppsMenu } from '@/components/layout/AppsMenu'
import { NotificationBell } from '@/components/layout/NotificationBell'
import { ThemePicker } from '@/components/layout/ThemePicker'
import { TimezoneButton } from '@/components/layout/TimezoneButton'
import { TimezoneHost } from '@/components/layout/TimezoneHost'
import { UserMenu } from '@/components/layout/UserMenu'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { useAccess } from '@/hooks/queries/access'
import { useAnalystMe } from '@/hooks/queries/aiAnalyst'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

import { AppSidebar } from './AppSidebar'
import { CommandMenu } from './CommandMenu'
import { trailFor } from './navigation'

/*
 * The analyst's launcher and panel bring the chat runtime (the AI SDK,
 * Markdown, charts) with them: loaded only for someone who can use it, and
 * after the shell, so the frame every page waits for stays small.
 */
const AiAnalystDock = lazy(async () => ({
  default: (await import('@/components/AiAnalyst/AiAnalystDock')).AiAnalystDock,
}))

/**
 * The frame every route renders inside: sidebar, header, content.
 *
 * The header is a fixed 56px on every page and carries the trail — the
 * page's own, or section › folder › page from the sidebar's map — so where
 * the user is reads the same everywhere. The sidebar collapses to icons on
 * desktop from a button beside its own name, and becomes a drawer on a phone,
 * which the header's button opens; the header is where search (centred), the
 * page's time zone, the preferences and the user menu are always reachable. The content is padded 16
 * on mobile and 24 from tablet up, and stops at 1600.
 *
 * For whoever may use the AI Analyst, the shell also carries it on every page
 * (plan §4.2): the floating launcher and the docked panel beside the page,
 * whose width the page gives up while it is open, and "Ask AI about this
 * page" in the command menu. Questions from the panel carry the page's path,
 * its trail as a title, and its search params.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })
  // The page's search params, as the router parsed them: its filters. Read from
  // the location rather than the leaf match, whose state changes on every step
  // of a transition and re-rendered the shell enough to stall navigation.
  const search = useLocation({ select: (location) => location.search })
  const [trailSlot, setTrailSlot] = useState<HTMLElement | null>(null)
  const analyst = useAnalystMe().data?.can_use === true
  const principalId = useAccess().data?.principal_id
  const dock = useAnalystDock()
  const onAnalystPage = /^\/ai-analyst(\/|$)/.test(pathname)
  const panelOpen = analyst && dock.open && !onAnalystPage

  return (
    <MotionConfig reducedMotion="user">
      <SidebarProvider>
        <a
          href="#main"
          className="bg-surface text-foreground sr-only rounded-md font-semibold shadow-md focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:px-3 focus:py-2"
        >
          {t('common.skipToContent')}
        </a>

        <AppSidebar />

        <TimezoneHost pathname={pathname}>
          <SidebarInset
            id="main"
            className={cn('min-w-0', PANEL_RESERVE_CLASS)}
            style={panelReserveStyle(panelOpen, dock.width)}
          >
            {/* Above the header, inside the main column: the sidebar is fixed,
              and a strip rendered above the whole shell sits under it. */}
            <MaintenanceNotices />
            {/* Three columns: the trail on the left, search in the middle of the
              bar wherever the trail ends, the tools on the right. Equal outer
              columns are what keep the search centred. */}
            <header className="bg-surface sticky top-0 z-20 grid h-14 shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b px-4">
              <div className="flex min-w-0 items-center gap-3">
                {/* Phones only: there the sidebar is a drawer, and this is how it
                  opens. On a desktop the sidebar folds from its own header. */}
                <SidebarTrigger className="size-9 md:hidden" aria-label={t('nav.openNavigation')} />
                {/* A page's own trail is portalled into the slot; the map's trail
                  stands down when one is there. */}
                <div className="group/trail flex min-w-0 items-center">
                  <div ref={setTrailSlot} className="contents" />
                  <Trail
                    pathname={pathname}
                    className="group-has-[[data-page-trail]]/trail:hidden"
                  />
                </div>
              </div>
              <CommandMenu onAskAi={analyst ? () => dock.setOpen(true) : undefined} />
              <div className="flex items-center justify-end gap-1">
                <TimezoneButton pathname={pathname} />
                <ThemePicker />
                <NotificationBell />
                {/* Every VentaWiz app ends its header the same way — the app
                    selector, then the account menu, where sign-out is — so a
                    person moving between apps finds them where they left them. */}
                <AppsMenu />
                <UserMenu />
              </div>
            </header>
            {/* Clipped, not scrolled: a scrolling box here would be the one every
              `sticky` inside a page sticks to, and it never scrolls itself. */}
            <div className="mx-auto w-full max-w-400 min-w-0 flex-1 overflow-x-clip p-4 md:p-6">
              <HeaderTrailProvider value={trailSlot}>{children}</HeaderTrailProvider>
            </div>
          </SidebarInset>
        </TimezoneHost>

        {analyst ? (
          // No fallback: the launcher appears once loaded, and nothing on the page waits for it.
          <Suspense fallback={null}>
            <AiAnalystDock
              dock={dock}
              pageContext={{
                path: pathname,
                title: pageTitle(pathname, t) ?? pathname,
                search: pageSearchOf(search),
              }}
              onAnalystPage={onAnalystPage}
              onOpenFullScreen={(conversationId) =>
                void navigate({ to: '/ai-analyst/c/$conversationId', params: { conversationId } })
              }
              onViewAll={() => void navigate({ to: '/ai-analyst' })}
              deepKey={principalId ? `aiAnalyst.deep.${principalId}` : undefined}
            />
          </Suspense>
        ) : null}
      </SidebarProvider>
    </MotionConfig>
  )
}

/** Section › group › page as one line: what the panel says a question was asked about. */
function pageTitle(pathname: string, t: TFunction): string | undefined {
  const trail = trailFor(pathname)
  if (!trail) return undefined
  const steps: ParseKeys[] = [trail.section.key]
  if (trail.group) steps.push(trail.group)
  if (trail.page) steps.push(trail.page.key)
  return steps.map((key) => t(key)).join(' › ')
}

/**
 * Section › group › page, from the sidebar's map — the header's trail for a
 * page that brings none of its own.
 */
function Trail({ pathname, className }: { pathname: string; className?: string }) {
  const { t } = useTranslation()
  const trail = trailFor(pathname)
  if (!trail) return null

  const steps: ParseKeys[] = [trail.section.key]
  if (trail.group) steps.push(trail.group)
  if (trail.page) steps.push(trail.page.key)

  return <Breadcrumbs trail={steps.map((key) => ({ label: t(key) }))} className={className} />
}

/**
 * The shell's silhouette, shown while its chunk loads.
 *
 * `autoCodeSplitting` puts the layout in a lazy chunk, so on a cold load there
 * is a moment with nothing on screen. This holds the exact geometry the real
 * shell will occupy — the sidebar's 260px width, the 56px header — so the sidebar does not shove the content sideways when it arrives.
 *
 * Deliberately not a spinner: a spinner says "wait", a silhouette says "wait,
 * and here is what is coming", and only one of them reserves the space.
 */
export function AppShellSkeleton() {
  return (
    <div className="bg-background flex h-full" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <aside className="bg-sidebar border-sidebar-border hidden w-65 shrink-0 border-r md:block">
        <div className="flex h-14 items-center gap-2.5 px-5">
          <span className="bg-logo size-7 rounded-md" />
        </div>
        <div className="flex flex-col gap-px px-2">
          <Skeleton className="bg-sidebar-hover h-9 w-full" />
          <Skeleton className="bg-sidebar-hover h-9 w-full" />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div data-slot="header" className="bg-surface h-14 border-b" />
        <div className="flex flex-col gap-4 p-4 md:p-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  )
}
